import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import * as DeviceInfo from 'expo-device';

const API_CONFIG = {
  BASE_URL: 'https://api.jobeng.ai',
  TIMEOUT: 30000,
  MAX_RETRIES: 3,
};

// Initialize axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
});

// Request interceptor for authentication
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Try to get token from SecureStore (for mobile)
      let token = await SecureStore.getItemAsync('jobeng_access_token');
      
      // Fallback to AsyncStorage if SecureStore not available
      if (!token) {
        token = await AsyncStorage.getItem('jobeng_access_token');
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      config.headers['X-Client-Version'] = '1.0.0';
      config.headers['X-Platform'] = Platform.OS;
      config.headers['X-Device-ID'] = await DeviceInfo.getDeviceId();
      
      return config;
    } catch (error) {
      console.error('Auth interceptor error:', error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration (401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = await SecureStore.getItemAsync('jobeng_refresh_token') || 
                             await AsyncStorage.getItem('jobeng_refresh_token');
        
        if (refreshToken) {
          const response = await axios.post(
            `${API_CONFIG.BASE_URL}/api/auth/refresh`,
            { refresh_token: refreshToken }
          );
          
          const { access_token, refresh_token } = response.data;
          
          // Store new tokens
          await SecureStore.setItemAsync('jobeng_access_token', access_token);
          await SecureStore.setItemAsync('jobeng_refresh_token', refresh_token);
          await AsyncStorage.setItem('jobeng_access_token', access_token);
          await AsyncStorage.setItem('jobeng_refresh_token', refresh_token);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Clear tokens and redirect to login
        await clearAuthTokens();
      }
    }
    
    // Handle network errors
    if (!error.response) {
      throw {
        message: 'Network error. Please check your connection.',
        isNetworkError: true,
        originalError: error,
      };
    }
    
    // Handle server errors
    if (error.response.status >= 500) {
      throw {
        message: 'Server error. Please try again later.',
        isServerError: true,
        status: error.response.status,
        originalError: error,
      };
    }
    
    // Handle specific error statuses
    switch (error.response.status) {
      case 403:
        throw {
          message: 'Access forbidden. You may not have permission.',
          isForbidden: true,
          originalError: error,
        };
      case 404:
        throw {
          message: 'Resource not found.',
          isNotFound: true,
          originalError: error,
        };
      case 422:
        throw {
          message: 'Validation error. Please check your input.',
          isValidationError: true,
          errors: error.response.data?.errors,
          originalError: error,
        };
      default:
        throw error;
    }
  }
);

// Clear auth tokens
const clearAuthTokens = async () => {
  try {
    await Promise.all([
      SecureStore.deleteItemAsync('jobeng_access_token'),
      SecureStore.deleteItemAsync('jobeng_refresh_token'),
      AsyncStorage.removeItem('jobeng_access_token'),
      AsyncStorage.removeItem('jobeng_refresh_token'),
    ]);
  } catch (error) {
    console.error('Error clearing auth tokens:', error);
  }
};

// Core API Methods
export const ERPApiService = {
  // Authentication
  login: async (email, password) => {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const { access_token, refresh_token } = response.data;
      
      // Store tokens
      await SecureStore.setItemAsync('jobeng_access_token', access_token);
      await SecureStore.setItemAsync('jobeng_refresh_token', refresh_token);
      await AsyncStorage.setItem('jobeng_access_token', access_token);
      await AsyncStorage.setItem('jobeng_refresh_token', refresh_token);
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  logout: async () => {
    await clearAuthTokens();
  },

  // Health Check
  healthCheck: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  },

  // ERP Dashboard
  getDashboardData: async () => {
    try {
      const response = await apiClient.get('/api/erp/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Notifications
  getNotifications: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/erp/notifications', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await apiClient.patch(`/notifications/${notificationId}`, {
        read: true
      });
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  markAllNotificationsAsRead: async () => {
    try {
      const response = await apiClient.post('/api/erp/notifications/mark-all-read');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Quick Actions
  getQuickActions: async () => {
    try {
      const response = await apiClient.get('/api/erp/quick-actions');
      return response.data;
    } catch (error) {
      console.error('Error fetching quick actions:', error);
      throw error;
    }
  },

  executeQuickAction: async (actionId, data = {}) => {
    try {
      const response = await apiClient.post(`/api/erp/quick-actions/${actionId}/execute`, data);
      return response.data;
    } catch (error) {
      console.error('Error executing quick action:', error);
      throw error;
    }
  },

  // CRM - Customers
  getCustomers: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/erp/customers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Try alternative endpoint
      try {
        const altResponse = await apiClient.get('/api/customers', { params });
        return altResponse.data;
      } catch (altError) {
        throw error;
      }
    }
  },

  getCustomerDetails: async (customerId) => {
    try {
      const response = await apiClient.get(`/api/customers/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      throw error;
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await apiClient.post('/api/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  updateCustomer: async (customerId, updates) => {
    try {
      const response = await apiClient.patch(`/api/customers/${customerId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  // Opportunities
  getOpportunities: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/erp/opportunities', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      // Try alternative endpoint
      try {
        const altResponse = await apiClient.get('/api/data/opportunities', { params });
        return altResponse.data;
      } catch (altError) {
        throw error;
      }
    }
  },

  getOpportunityDetails: async (opportunityId) => {
    try {
      const response = await apiClient.get(`/api/data/opportunities/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunity details:', error);
      throw error;
    }
  },

  createOpportunity: async (opportunityData) => {
    try {
      const response = await apiClient.post('/api/data/opportunities', opportunityData);
      return response.data;
    } catch (error) {
      console.error('Error creating opportunity:', error);
      throw error;
    }
  },

  updateOpportunity: async (opportunityId, updates) => {
    try {
      const response = await apiClient.patch(`/api/data/opportunities/${opportunityId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating opportunity:', error);
      throw error;
    }
  },

  // Analytics & KPIs
  getKPIMetrics: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/erp/analytics/kpis', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching KPI metrics:', error);
      throw error;
    }
  },

  getModuleAnalytics: async (moduleId, params = {}) => {
    try {
      const response = await apiClient.get(`/api/erp/modules/${moduleId}/analytics`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching module analytics:', error);
      throw error;
    }
  },

  // Reports
  getReports: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/reports', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  },

  generateReport: async (reportType, data = {}) => {
    try {
      const response = await apiClient.post('/api/reports/generate', {
        report_type: reportType,
        ...data
      });
      return response.data;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  // Users & Employees
  getEmployees: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/employees', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  getEmployeeDetails: async (employeeId) => {
    try {
      const response = await apiClient.get(`/api/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee details:', error);
      throw error;
    }
  },

  createEmployee: async (employeeData) => {
    try {
      const response = await apiClient.post('/api/employees', employeeData);
      return response.data;
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  },

  updateEmployee: async (employeeId, updates) => {
    try {
      const response = await apiClient.patch(`/api/employees/${employeeId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  },

  // Projects & Tasks
  getProjects: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/projects', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  getProjectDetails: async (projectId) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  getTasks: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/data/tasks', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  getTaskDetails: async (taskId) => {
    try {
      const response = await apiClient.get(`/api/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task details:', error);
      throw error;
    }
  },

  // Inventory Management
  getInventoryItems: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/data/inventory_items', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  },

  getLowStockItems: async (threshold = 10) => {
    try {
      const response = await apiClient.get('/api/data/inventory_items/low-stock', {
        params: { threshold }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching low stock items:', error);
      throw error;
    }
  },

  createInventoryItem: async (itemData) => {
    try {
      const response = await apiClient.post('/api/data/inventory_items', itemData);
      return response.data;
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw error;
    }
  },

  updateInventoryItem: async (itemId, updates) => {
    try {
      const response = await apiClient.patch(`/api/data/inventory_items/${itemId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },

  // Finance & Invoices
  getInvoices: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/invoices', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  getInvoiceDetails: async (invoiceId) => {
    try {
      const response = await apiClient.get(`/api/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      throw error;
    }
  },

  createInvoice: async (invoiceData) => {
    try {
      const response = await apiClient.post('/api/invoices', invoiceData);
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  getPayments: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/payments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  },

  // Time & Attendance
  getTimeEntries: async (params = {}) => {
    try {
      const response = await apiClient.get('/time-entries', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
    }
  },

  getAttendance: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/attendance', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching attendance:', error);
      throw error;
    }
  },

  // Leave Management
  getLeaveRequests: async (params = {}) => {
    try {
      const response = await apiClient.get('/leave-requests', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      throw error;
    }
  },

  createLeaveRequest: async (leaveData) => {
    try {
      const response = await apiClient.post('/leave-requests', leaveData);
      return response.data;
    } catch (error) {
      console.error('Error creating leave request:', error);
      throw error;
    }
  },

  // Expense Management
  getExpenses: async (params = {}) => {
    try {
      const response = await apiClient.get('/expenses', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  },

  createExpense: async (expenseData) => {
    try {
      const response = await apiClient.post('/expenses', expenseData);
      return response.data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  },

  // Companies & Departments
  getCompanies: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/companies', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  },

  getDepartments: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/departments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  // Settings
  getSettings: async () => {
    try {
      const response = await apiClient.get('/api/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  updateSettings: async (settings) => {
    try {
      const response = await apiClient.patch('/api/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  // AI & Analytics
  getAIInsights: async (data) => {
    try {
      const response = await apiClient.post('/api/data/ai_insights', data);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      throw error;
    }
  },

  getAIRecommendations: async (data) => {
    try {
      const response = await apiClient.post('/api/data/ai_recommendations', data);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI recommendations:', error);
      throw error;
    }
  },

  // Deals
  getDeals: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/data/deals', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  },

  createDeal: async (dealData) => {
    try {
      const response = await apiClient.post('/api/data/deals', dealData);
      return response.data;
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  updateDeal: async (dealId, updates) => {
    try {
      const response = await apiClient.patch(`/api/data/deals/${dealId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  // Dynamic Tables - Generic CRUD operations
  getDynamicTableData: async (tableName, params = {}) => {
    try {
      const response = await apiClient.get(`/api/data/${tableName}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching data from ${tableName}:`, error);
      throw error;
    }
  },

  createDynamicTableRecord: async (tableName, data) => {
    try {
      const response = await apiClient.post(`/api/data/${tableName}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating record in ${tableName}:`, error);
      throw error;
    }
  },

  updateDynamicTableRecord: async (tableName, id, updates) => {
    try {
      const response = await apiClient.patch(`/api/data/${tableName}/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating record in ${tableName}:`, error);
      throw error;
    }
  },

  deleteDynamicTableRecord: async (tableName, id) => {
    try {
      const response = await apiClient.delete(`/api/data/${tableName}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting record from ${tableName}:`, error);
      throw error;
    }
  },

  // File Upload
  uploadFile: async (file, options = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...options
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Export Data
  exportData: async (format, dataTypes, options = {}) => {
    try {
      const response = await apiClient.post('/api/data/exports', {
        format,
        data_types: dataTypes,
        ...options
      });
      
      return response.data;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  },

  // API Endpoints Discovery
  getAvailableEndpoints: async () => {
    try {
      const response = await apiClient.get('/api/endpoints');
      return response.data;
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      throw error;
    }
  },

  // Database Tables Discovery
  getAvailableTables: async () => {
    try {
      const response = await apiClient.get('/api/tables');
      return response.data;
    } catch (error) {
      console.error('Error fetching tables:', error);
      throw error;
    }
  },

  // Test connection
  testConnection: async () => {
    try {
      const response = await apiClient.get('/api/test/mobile');
      return response.data;
    } catch (error) {
      console.error('Test connection failed:', error);
      throw error;
    }
  },

  // Get server statistics
  getServerStats: async () => {
    try {
      const response = await apiClient.get('/');
      return response.data;
    } catch (error) {
      console.error('Error fetching server stats:', error);
      throw error;
    }
  }
};

export default ERPApiService;
